/**
 * SessionRoster - Full roster view for bulk attendance marking
 */

import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useCallback, useEffect, useState } from "react"
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiMapPin,
  FiSave,
} from "react-icons/fi"

import {
  useBulkMarkAttendance,
  useSessionRoster,
} from "@/hooks/api/useCoachClasses"
import type {
  AttendanceStatus,
  LocalAttendanceState,
  RosterStudent,
  StudentAttendanceEntry,
} from "@/types/coach"

import { CoachAttendanceForm } from "./CoachAttendanceForm"

interface SessionRosterProps {
  sessionId: string
  onBack: () => void
}

export function SessionRoster({ sessionId, onBack }: SessionRosterProps) {
  const { data: roster, isLoading, error } = useSessionRoster(sessionId)
  const bulkMarkAttendance = useBulkMarkAttendance()

  // Local state for attendance edits
  const [localState, setLocalState] = useState<LocalAttendanceState>({})

  // Initialize local state when roster loads
  useEffect(() => {
    if (roster?.students) {
      const initial: LocalAttendanceState = {}
      for (const student of roster.students) {
        initial[student.student_id] = {
          status: student.status || "present",
          skills_practiced: student.skills_practiced || {},
          instructor_notes: student.instructor_notes || "",
        }
      }
      setLocalState(initial)
    }
  }, [roster?.students])

  const handleStatusChange = useCallback(
    (studentId: string, status: AttendanceStatus) => {
      setLocalState((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          status,
        },
      }))
    },
    [],
  )

  const handleSkillsChange = useCallback(
    (studentId: string, skills: Record<string, boolean>) => {
      setLocalState((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          skills_practiced: skills,
        },
      }))
    },
    [],
  )

  const handleNotesChange = useCallback((studentId: string, notes: string) => {
    setLocalState((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        instructor_notes: notes,
      },
    }))
  }, [])

  const handleMarkAllPresent = useCallback(() => {
    if (!roster?.students) return

    setLocalState((prev) => {
      const updated = { ...prev }
      for (const student of roster.students) {
        updated[student.student_id] = {
          ...updated[student.student_id],
          status: "present",
        }
      }
      return updated
    })
  }, [roster?.students])

  const handleSaveAll = useCallback(async () => {
    if (!roster?.students) return

    const entries: StudentAttendanceEntry[] = roster.students.map(
      (student: RosterStudent) => ({
        student_id: student.student_id,
        enrollment_id: student.enrollment_id,
        status: localState[student.student_id]?.status || "present",
        skills_practiced:
          localState[student.student_id]?.skills_practiced || null,
        instructor_notes:
          localState[student.student_id]?.instructor_notes || null,
      }),
    )

    await bulkMarkAttendance.mutateAsync({
      sessionId,
      data: { entries },
    })
  }, [roster?.students, localState, sessionId, bulkMarkAttendance])

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <VStack gap={4}>
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading roster...</Text>
        </VStack>
      </Flex>
    )
  }

  if (error || !roster) {
    return (
      <VStack gap={4} py={8}>
        <Text color="red.500">Failed to load roster</Text>
        <Button onClick={onBack} variant="outline">
          Go Back
        </Button>
      </VStack>
    )
  }

  const markedCount = Object.keys(localState).length

  return (
    <VStack align="stretch" gap={4}>
      {/* Header */}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
        <HStack gap={3}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            aria-label="Go back"
          >
            <FiArrowLeft />
          </Button>
          <Box>
            <Heading size="md">{roster.class_name}</Heading>
            <HStack gap={3} color="gray.600" fontSize="sm" mt={1}>
              <Flex align="center" gap={1}>
                <FiClock size={14} />
                <Text>{roster.start_time}</Text>
              </Flex>
              <Flex align="center" gap={1}>
                <FiMapPin size={14} />
                <Text>{roster.location}</Text>
              </Flex>
            </HStack>
          </Box>
        </HStack>

        <Flex
          gap={2}
          direction={{ base: "column", sm: "row" }}
          w={{ base: "100%", sm: "auto" }}
        >
          <Button
            size="sm"
            variant="outline"
            onClick={handleMarkAllPresent}
            disabled={bulkMarkAttendance.isPending}
            minH="44px"
          >
            <FiCheckCircle />
            Mark All Present
          </Button>
          <Button
            size="sm"
            colorPalette="blue"
            onClick={handleSaveAll}
            disabled={bulkMarkAttendance.isPending}
            minH="44px"
          >
            {bulkMarkAttendance.isPending ? <Spinner size="sm" /> : <FiSave />}
            Save All ({markedCount})
          </Button>
        </Flex>
      </Flex>

      {/* Student list */}
      {roster.students.length === 0 ? (
        <Box textAlign="center" py={8} color="gray.500">
          <Text>No students enrolled in this class</Text>
        </Box>
      ) : (
        <VStack align="stretch" gap={3}>
          {roster.students.map((student) => (
            <CoachAttendanceForm
              key={student.student_id}
              student={student}
              status={localState[student.student_id]?.status || "present"}
              skillsPracticed={
                localState[student.student_id]?.skills_practiced || {}
              }
              notes={localState[student.student_id]?.instructor_notes || ""}
              skillsForLevel={roster.skills_for_level}
              onStatusChange={(status) =>
                handleStatusChange(student.student_id, status)
              }
              onSkillsChange={(skills) =>
                handleSkillsChange(student.student_id, skills)
              }
              onNotesChange={(notes) =>
                handleNotesChange(student.student_id, notes)
              }
              disabled={bulkMarkAttendance.isPending}
            />
          ))}
        </VStack>
      )}
    </VStack>
  )
}
